import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {faCalendarDays, faDownload, faFileContract, faFileText, faPaperPlane} from '@fortawesome/pro-duotone-svg-icons';
import {
  ButtonTypeEnum,
  IspBadgeColoursService,
  IspColoursEnum,
} from '@i-supplier/angular-shared-module';
import {TranslateService} from '@ngx-translate/core';
import {BsModalService} from 'ngx-bootstrap/modal';
import {Subscription} from 'rxjs';
import {ModalConfirmComponent} from '../../../shared/components/modal-confirm/modal-confirm.component';
import {PurchaseOrderStatusEnum} from '../../../shared/enums/Purchase-order.status.enum';
import {PurchaseOrderModel} from '../../../shared/models/purchase-order/purchase-order.model';
import {LocalizedDatePipe} from '../../../shared/pipes/localized-date/localized-date.pipe';
import {PurchaseOrderService} from '../services/purchase-order-service';
import {DomSanitizer} from '@angular/platform-browser';
import {ToastrService} from 'ngx-toastr';

@Component({
  selector: 'purchase-order-view',
  templateUrl: './purchase-order-view.component.html',
  styleUrls: ['./purchase-order-view.component.scss']
})
export class PurchaseOrderViewComponent implements OnInit, OnDestroy {

  constructor(
    private modalService: BsModalService,
    private purchaseOrderService: PurchaseOrderService,
    protected badgeService: IspBadgeColoursService,
    private translateService: TranslateService,
    protected toastrService: ToastrService,
    private activatedRoute: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private router: Router,
  ) {}
  protected readonly PurchaseOrderStatusEnum = PurchaseOrderStatusEnum;
  protected readonly ButtonTypeEnum = ButtonTypeEnum;
  protected readonly IspColours = IspColoursEnum;
  protected readonly faDownload = faDownload;
  private subscriptions: Subscription[] = [];
  itemsList = [];
  selectedPurchaseOrder: PurchaseOrderModel;
  view: string;
  supplierPOFile: any;
  loaders: { [key: string]: boolean } = {};

  protected readonly faPaperPlane = faPaperPlane;

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  load(): void {
    this.purchaseOrderService.getPurchaseOrderById(this.activatedRoute.snapshot.paramMap.get('purchaseOrderId')).subscribe({
      next: (res: any) => {
        this.selectedPurchaseOrder = res;

        this.itemsList.push(
          {
            id: 0,
            header: this.translateService.instant('Purchase_orders.request_number'),
            subheader: this.selectedPurchaseOrder.requestNumber,
            status: {icon: faFileText, color: 'tertiary'}
          },
          {
            id: 1,
            header: this.translateService.instant('global.common.creation-date'),
            subheader: (new LocalizedDatePipe(this.translateService)).transform(this.selectedPurchaseOrder.createdDate),
            status: {icon: faCalendarDays, color: 'secondary'}
          },
          {
            id: 2,
            header: this.translateService.instant('Purchase_orders.signatory'),
            subheader: this.selectedPurchaseOrder.hasOwnProperty('recipientFirstName') ? `${this.selectedPurchaseOrder.recipientFirstName} ${this.selectedPurchaseOrder.recipientLastName}` : this.selectedPurchaseOrder.recipientLastName,
            subtext: this.selectedPurchaseOrder.recipientEmail,
            status: {icon: faFileContract, color: 'success'}
          },
        );
        this.view = this.selectedPurchaseOrder.status;

        this.purchaseOrderService.getPurchaseOrderFile(this.selectedPurchaseOrder.id, this.selectedPurchaseOrder.status).subscribe({
          next: (file: any) => this.displayBlob(file.body),
          error: () => this.toastrService.error(this.translateService.instant('global.ts.error'))
        });
      },
      error: () => this.router.navigate(['/purchase-orders']).then()
    });
  }

  getAlertType() {
    switch (this.selectedPurchaseOrder.status) {
      case PurchaseOrderStatusEnum.GENERATED: {
        return 'info';
      }
      case PurchaseOrderStatusEnum.CANCELLED: {
        return 'warning';
      }
      case PurchaseOrderStatusEnum.VALIDATED: {
        return 'success';
      }
      case PurchaseOrderStatusEnum.REFUSED_BY_SUPPLIER: {
        return 'danger';
      }
      case PurchaseOrderStatusEnum.EXPIRED: {
        return 'warning';
      }
      default : {
        return 'info';
      }
    }
  }

  downloadPurchaseOrder() {
    this.purchaseOrderService.getPurchaseOrderFile(this.selectedPurchaseOrder.id, this.selectedPurchaseOrder.status).subscribe({
      next: (res: any) => this.purchaseOrderService.handlePurchaseOrderFileDownload(res, this.selectedPurchaseOrder.contractNumber),
      error: () => this.toastrService.error(this.translateService.instant('global.ts.error'))
    });
  }

  private displayBlob(data: Blob) {
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result as string;
      this.supplierPOFile = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    };
    reader.readAsDataURL(data);
    this.loaders['displayBlob'] = false;
  }

  sendSignatureLinks(): void {
    const initialState = {
      message: this.translateService.instant('Purchase_orders.resend-link-confirm'),
    };
    const modalRef = this.modalService.show(ModalConfirmComponent, {class: 'modal-lg', initialState});
    this.subscriptions.push(modalRef.content.actionConfirmed.subscribe(reason => {
      this.purchaseOrderService.generateSignatureLinks(this.selectedPurchaseOrder.id).subscribe({
        next: () => {
          this.toastrService.info(this.translateService.instant('Purchase_orders.resend-link-toastr'));
        },
        error: () => this.toastrService.error(this.translateService.instant('global.ts.error'))
      });
    }));
  }
}
